export interface Timesheet {
  id?: string;
  userId?: string;
  /**
   * Mission rattachée au CRA (multi-mission). `null`/absent en mono-mission et pour
   * les internes — dans ce cas aucun `missionId` ne transite (rétrocompat mono-mission).
   */
  missionId?: string | null;
  year: number;
  month: number;
  timesheetStatus: TimesheetStatus;
  workingDays: number;
  totalRemoteWorkDays: number;
  totalWorkedDays: number;
  totalAbsenceDays: number;
  hasOnCallDuty: boolean;
  activities: Activity[];
  comment: string;
  /**
   * Motif de refus / renvoi en correction laissé par le validateur RH (BE, effacé à la
   * validation). Présent → le CRA a été renvoyé au consultant pour correction.
   */
  reviewerComment?: string | null;
  /**
   * Origine du renvoi (BE, dérivé du dernier audit) : `rejected` = refus admin,
   * `reopened` = réouverture neutre pour correction. Pilote le ton du bandeau
   * consultant (ambre vs bleu). Absent → traité comme `rejected` (repli).
   */
  correctionContext?: 'rejected' | 'reopened' | null;
  /**
   * Validation par un validateur client externe (société intermédiaire). `NONE`/absent = pas de
   * validateur. `PENDING` = le CRA attend le retour du client (le consultant peut relancer).
   */
  externalValidationStatus?: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'OVERRIDDEN' | null;
  externalValidatorName?: string | null;
  externalValidationReminderCount?: number | null;
  externalValidationLastReminderAt?: string | null;
  /** Motif si le validateur client a refusé (REJECTED). */
  externalValidationComment?: string | null;
  /** Consultation du dernier lien de validation envoyé — voir `ExternalValidatorBadge` (#841). */
  externalValidationLinkViewedAt?: string | null;
}

export type OnCallDutyEntry = {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  crossMidnight: boolean;
  minutes: number;
  type: OnCallDutyType;
  comments?: string;
};

export type OnCallDutyPayload = Omit<OnCallDutyEntry, 'id'>;

export interface Activity {
  id?: string;
  type: ActivityType;
  name: string;
  /** Mission liée (pour les activités de type PROJECT). Absent pour les absences / jours non facturés. */
  missionId?: string;
  daysWork: { [date: string]: number };
  remoteWorkDays: { [date: string]: boolean };
  onCallDuties?: OnCallDutyEntry[];
}

export interface ActivityOption {
  id: string;
  type: ActivityType;
  value: string;
  label: string;
  group: string;
  /** Aide affichée au consultant (tooltip/sous le sélecteur). `null` si absente. */
  description?: string | null;
  /** Mission liée (pour les options de type PROJECT dérivées des missions du consultant). */
  missionId?: string;
  allowRemoteFlag?: boolean;
  requiresJustification?: boolean;
  justificationHint?: string | null;
}

export interface Week {
  week: string;
  span: number;
}

export interface ToastType {
  id: number;
  type: ToastTypeEnum;
  message: string;
}

export enum ToastTypeEnum {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
}

export enum OnCallDutyType {
  HNO = 'HNO',
  Astreinte = 'Astreinte',
  autre = 'autre',
}

export enum TimesheetStatus {
  OPEN = 'open',
  PARTIAL_COMPLETED = 'partial',
  COMPLETED = 'completed',
  SUBMITTED = 'submitted',
  VALIDATED = 'validated',
}

export enum ActivityType {
  OTHER = 'other',
  PROJECT = 'project',
  ABSENCE_JUSTIFIED = 'justifiedAbsence',
  ABSENCE_TO_BE_JUSTIFIED = 'absenceToBeJustified',
}

export type CraStatus = 'COMPLETED' | 'IN_PROGRESS' | 'LATE';

export interface TimesheetCompletion {
  year: number;
  month: number; // 1..12
  periodStart: string; // ISO (YYYY-MM-01)
  periodEnd: string; // ISO (YYYY-MM-<last>)
  periodLabel: string; // "Mai 2025"
  percent: number; // 0..100
  filledDays: number; // nb jours saisis
  totalDays: number; // nb jours ouvrés du mois
  missingDays: number; // totalDays - filledDays
  status: CraStatus; // pour teinter l’UI
  lastUpdated: string; // ISO
}

export interface TimesheetCompletionSummaryItem {
  year: number;
  month: number;
  percent: number;
  status: CraStatus;
}

export interface TimesheetCompletionSummary {
  userId: string;
  generatedAt: string;
  items: TimesheetCompletionSummaryItem[];
}
